import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { characterClassApi, spellApi, spriteApi } from '../../../api/adminApi';
import { useTranslation } from '../../../i18n/useTranslation';
import type { CharacterClassData, SpellData, PrimaryElement, PrimaryAttribute } from '../../../types/adminTypes';

const ELEMENTS: PrimaryElement[]   = ['Fire', 'Earth', 'Air', 'Water', 'Neutral'];
const ATTRIBUTES: PrimaryAttribute[] = ['strength', 'intelligence', 'agility', 'luck', 'wisdom'];

const ELEMENT_LABELS: Record<PrimaryElement, { en: string; pt: string; icon: string }> = {
  Fire:    { en: 'Fire',    pt: 'Fogo',    icon: '🔥' },
  Earth:   { en: 'Earth',   pt: 'Terra',   icon: '🌿' },
  Air:     { en: 'Air',     pt: 'Ar',      icon: '💨' },
  Water:   { en: 'Water',   pt: 'Água',    icon: '💧' },
  Neutral: { en: 'Neutral', pt: 'Neutro',  icon: '⚪' },
};

const ATTR_LABELS: Record<PrimaryAttribute, { en: string; pt: string; icon: string }> = {
  strength:     { en: 'Strength',     pt: 'Força',         icon: '💪' },
  intelligence: { en: 'Intelligence', pt: 'Inteligência',  icon: '🧠' },
  agility:      { en: 'Agility',      pt: 'Agilidade',     icon: '🏃' },
  luck:         { en: 'Luck',         pt: 'Sorte',         icon: '🍀' },
  wisdom:       { en: 'Wisdom',       pt: 'Sabedoria',     icon: '📖' },
};

// ── Sprite Picker (reutilizado do MonsterFormPage) ────────────────────────────

function SpritePicker({ value, onChange, sprites }: { value: string; onChange: (name: string) => void; sprites: any[] }) {
  const PREVIEW = 72;

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
      <button type="button" onClick={() => onChange('')}
        style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          padding: '8px 12px', borderRadius: 6,
          border: `2px solid ${value === '' ? 'var(--accent)' : 'var(--border)'}`,
          background: value === '' ? 'rgba(68,204,136,0.12)' : 'var(--surface)',
          cursor: 'pointer', minWidth: 80,
        }}
      >
        <div style={{
          width: PREVIEW, height: PREVIEW, display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, background: 'var(--input-bg)', borderRadius: 4, color: 'var(--text-dim)',
        }}>✕</div>
        <span style={{ fontSize: 11, color: value === '' ? 'var(--accent)' : 'var(--text-dim)', fontWeight: value === '' ? 700 : 400 }}>
          Nenhum
        </span>
      </button>
      {sprites.filter(s => s.type === 'class').map((s) => {
        const fileId = (s.filename || s.name + '.png').replace('.png', '');
        const selected = value === fileId;
        return (
          <button key={s.name} type="button" onClick={() => onChange(fileId)}
            style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
              padding: '8px 12px', borderRadius: 6,
              border: `2px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
              background: selected ? 'rgba(68,204,136,0.12)' : 'var(--surface)',
              cursor: 'pointer', minWidth: 80,
            }} title={s.label}
          >
            <div style={{ width: PREVIEW, height: PREVIEW, overflow: 'hidden', position: 'relative', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--input-bg)' }}>
              <img src={`/sprites/${fileId}.png?v=${Date.now()}`} alt={s.label}
                style={{ position: 'absolute', top: 0, left: 0, width: `calc(${PREVIEW}px * ${s.cols || 3})`, height: 'auto', imageRendering: 'pixelated', display: 'block' }} />
            </div>
            <span style={{ fontSize: 11, color: selected ? 'var(--accent)' : 'var(--text-dim)', fontWeight: selected ? 700 : 400 }}>
              {s.icon} {s.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Stat Field (reutilizado) ──────────────────────────────────────────────────

function StatField({ label, field, value, onChange, min = 0 }: {
  label: string; field: string; value: number; onChange: (field: string, v: number) => void; min?: number;
}) {
  return (
    <div className="formGroup">
      <label className="formLabel">{label}</label>
      <input type="number" className="formInput" value={value} min={min}
        onChange={(e) => onChange(field, Math.max(min, parseInt(e.target.value, 10) || 0))} />
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function ClassFormPage() {
  const { id }   = useParams<{ id: string }>();
  const isEdit   = Boolean(id);
  const navigate = useNavigate();
  const { t, language } = useTranslation();

  const emptyForm: CharacterClassData = {
    slug: 'warrior', display_name: '', description: '',
    primary_element: 'Earth', primary_attribute: 'strength',
    sprite: '', icon_url: '',
    hp_max: 200, ap_max: 6, mp_max: 3,
    strength: 0, intelligence: 0, agility: 0, luck: 0, wisdom: 0,
    initiative: 10, dodge: 0, block: 0, spell_ids: [],
  };

  const [form,    setForm]    = useState<CharacterClassData>(emptyForm);
  const [spells,  setSpells]  = useState<SpellData[]>([]);
  const [sprites, setSprites] = useState<any[]>([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState('');

  useEffect(() => { 
    void spellApi.list().then(setSpells).catch(() => {});
    void spriteApi.list().then(setSprites).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    setLoading(true);
    void characterClassApi.get(id!)
      .then((c) => setForm(c))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  const setField = (field: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const setStatField = (field: string, value: number) => setField(field, value);

  const toggleSpell = (spellId: string) => {
    setForm((prev) => ({
      ...prev,
      spell_ids: prev.spell_ids.includes(spellId)
        ? prev.spell_ids.filter((s) => s !== spellId)
        : [...prev.spell_ids, spellId],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.display_name.trim()) { setError(t('classForm.nameRequired')); return; }

    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await characterClassApi.update(id!, form);
      } else {
        await characterClassApi.create(form);
      }
      navigate('/admin/classes');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <>
      <div className="adminHeader"><h1>{t('common.loading')}</h1></div>
      <div className="adminContent"><div className="spinner" /></div>
    </>
  );

  const lang = language as 'en' | 'pt';

  return (
    <>
      <div className="adminHeader">
        <h1>{isEdit ? `✏ ${t('classForm.titleEdit')}: ${form.display_name}` : `🛡️ ${t('classForm.titleNew')}`}</h1>
      </div>

      <div className="adminContent">
        {error && <div className="alertError">{error}</div>}

        <form onSubmit={(e) => void handleSubmit(e)} className="adminForm">

          {/* ── Identity ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('classForm.identity')}</div>
            <div className="formGrid">
              <div className="formGroup">
                <label className="formLabel">{t('classForm.slug')}</label>
                <input type="text" className="formInput" value={form.slug}
                  onChange={(e) => setField('slug', e.target.value.toLowerCase().replace(/[^a-z_]/g, ''))}
                  disabled={isEdit}
                  style={isEdit ? { opacity: 0.6, cursor: 'not-allowed' } : {}}
                />
                {isEdit && <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{t('classForm.slugLocked')}</span>}
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('classForm.displayName')}</label>
                <input type="text" className="formInput" value={form.display_name}
                  onChange={(e) => setField('display_name', e.target.value)}
                  placeholder="Ex: Guerreiro" required />
              </div>
              <div className="formGroup formGroupFull">
                <label className="formLabel">{t('classForm.description')}</label>
                <textarea className="formTextarea" value={form.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder={t('classForm.descPlaceholder')} />
              </div>
            </div>
          </div>

          {/* ── Element & Attribute ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('classForm.elementAndAttr')}</div>
            <div className="formGrid">
              <div className="formGroup">
                <label className="formLabel">{t('classForm.primaryElement')}</label>
                <select className="formSelect" value={form.primary_element}
                  onChange={(e) => setField('primary_element', e.target.value)}>
                  {ELEMENTS.map((el) => (
                    <option key={el} value={el}>
                      {ELEMENT_LABELS[el].icon} {ELEMENT_LABELS[el][lang]}
                    </option>
                  ))}
                </select>
              </div>
              <div className="formGroup">
                <label className="formLabel">{t('classForm.primaryAttribute')}</label>
                <select className="formSelect" value={form.primary_attribute}
                  onChange={(e) => setField('primary_attribute', e.target.value)}>
                  {ATTRIBUTES.map((attr) => (
                    <option key={attr} value={attr}>
                      {ATTR_LABELS[attr].icon} {ATTR_LABELS[attr][lang]}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p style={{ fontSize: 11, color: 'var(--text-dim)', padding: '0 18px 14px' }}>
              {t('classForm.elementNote')}
            </p>
          </div>

          {/* ── Sprite ── */}
          <div className="adminCard">
            <div className="adminCardHeader">Sprite</div>
            <div style={{ padding: '14px 18px' }}>
              <SpritePicker value={form.sprite} onChange={(name) => setField('sprite', name)} sprites={sprites} />
            </div>
          </div>

          {/* ── Default Stats ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('classForm.defaultStats')}</div>
            <div className="statsGrid">
              <StatField label="HP Max" field="hp_max" value={form.hp_max} onChange={setStatField} min={1} />
              <StatField label="AP Max" field="ap_max" value={form.ap_max} onChange={setStatField} min={1} />
              <StatField label="MP Max" field="mp_max" value={form.mp_max} onChange={setStatField} />
              <StatField label={t('classForm.inventoryCapacity')} field="inventory_capacity" value={form.inventory_capacity || 50} onChange={setStatField} min={1} />
            </div>
          </div>

          {/* ── Attributes ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('classForm.attributes')}</div>
            <div className="statsGrid">
              <StatField label={`💪 ${t('monsterForm.strength')}`}     field="strength"     value={form.strength}     onChange={setStatField} />
              <StatField label={`🧠 ${t('monsterForm.intelligence')}`} field="intelligence" value={form.intelligence} onChange={setStatField} />
              <StatField label={`🏃 ${t('monsterForm.agility')}`}      field="agility"      value={form.agility}      onChange={setStatField} />
              <StatField label={`🍀 ${t('monsterForm.luck')}`}         field="luck"         value={form.luck}         onChange={setStatField} />
              <StatField label={`📖 ${t('monsterForm.wisdom')}`}       field="wisdom"       value={form.wisdom}       onChange={setStatField} />
              <StatField label={`⚡ ${t('monsterForm.initiative')}`}   field="initiative"   value={form.initiative}   onChange={setStatField} />
              <StatField label={`🛡 ${t('monsterForm.dodge')}`}        field="dodge"        value={form.dodge}        onChange={setStatField} />
              <StatField label={`🧱 ${t('monsterForm.block')}`}        field="block"        value={form.block}        onChange={setStatField} />
            </div>
          </div>

          {/* ── Spells ── */}
          <div className="adminCard">
            <div className="adminCardHeader">{t('classForm.spells')}</div>
            <div className="spellChips">
              {spells.length === 0
                ? <span style={{ color: 'var(--text-dim)' }}>{t('monsterForm.spells.loading')}</span>
                : spells.map((s) => (
                    <button
                      type="button" key={s.id}
                      className={`spellChip${form.spell_ids.includes(s.id) ? ' selected' : ''}`}
                      onClick={() => toggleSpell(s.id)}
                    >
                      <span style={{ fontWeight: 600 }}>{t(`spell.${s.id}.name` as any) || s.name}</span>
                      <span className="spellCost">{s.ap_cost} AP</span>
                    </button>
                  ))
              }
            </div>
          </div>

          {/* ── Actions ── */}
          <div className="formActions">
            <button type="submit" className="btn btnPrimary" disabled={saving}>
              {saving ? t('common.saving') : isEdit ? t('classForm.saveChanges') : t('classForm.create')}
            </button>
            <button type="button" className="btn btnSecondary" onClick={() => navigate('/admin/classes')}>
              {t('common.cancel')}
            </button>
          </div>

        </form>
      </div>
    </>
  );
}
